/* eslint-disable no-bitwise */

import { UntypedFormGroup } from "@angular/forms";
import { of } from "rxjs";
import { Cellstyle, HotCellText } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition } from "~app/common/components/hot-doc/hotdoc/doc";
import { HotRow } from "~app/common/components/hot-doc/hotdoc/row";
import { Action, InputMode, PromptInput } from "~app/common/components/modal/common/prompt-dialog/prompt-dialog.component";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { Diameterklass } from "../../produktinstruktioner-details/matris/Diameterklass";
import { ProduktmatrisGrid } from "../../produktinstruktioner-details/matris/ProduktmatrisGrid";
import { AddEditLengthCell } from "../../produktinstruktioner-details/matris/cells/addEditLengthCell";
import { RadprisjusteringNumberCell } from "../../produktinstruktioner-details/matris/cells/radprisjusteringNumberCell";
import { ProduktdefinitionHeaderData } from "../ProduktdefinitionHeaderData";
import { BaseMatrix } from "./BaseMatrix";

export class RadprisjusteringarMatris extends BaseMatrix {
    constructor(grid: ProduktmatrisGrid,
        form: UntypedFormGroup,
        headerData: ProduktdefinitionHeaderData,
        canEdit: boolean,
        private readonly defaultRadprisValue: () => number) {
        super(grid, form, headerData, canEdit);
    }

    getRadprisjusteringar(raskModalService: RaskModalService, form: UntypedFormGroup, afterChanges: () => any): HotDoc {
        const colFactory = () => this.buildRadprisjusteringarColumnDefinitions({
            readOnly: !this.canEdit,
            type: "numeric"
        });
        const rowFactory = (doc: HotDoc) => this.buildRadprisjusteringarRowDefinitions(doc, raskModalService);
        const settingsFactory = (doc: HotDoc) => this.sharedSettings(doc, raskModalService, {
            outsideClickDeselects: this.outsideClickDeselector,
            afterChange: (changes, source) => {
                afterChanges();
            }
        });

        return new HotDoc(colFactory, rowFactory, settingsFactory);
    }

    protected getEditRadprisDiameterklassCell(doc: HotDoc, diameterklass: Diameterklass, raskModalService: RaskModalService, icon = this.editIcon) {
        return new AddEditLengthCell(
            Cellstyle.Readonly | Cellstyle.AlignLeft | Cellstyle.VerticalAlignMiddle,
            { rowSpan: 1 },
            icon,
            () => this.onAddOrEditRadprisDiameter(doc, diameterklass, raskModalService));
    }

    private buildAddPrisjusteringRow(row: HotRow, doc: HotDoc, raskModalService: RaskModalService) {
        if (this.canEdit) {
            row.add(BaseMatrix.settingsCellName, this.getEditRadprisDiameterklassCell(doc, null, raskModalService, BaseMatrix.addIcon));
        }

        row.addText(BaseMatrix.rowHeaderCellName, "", Cellstyle.Readonly);

        this.grid.langdklasser.forEach(langdklass => {
            row.addText(this.langdCellName(langdklass.startlangd), "", Cellstyle.Readonly);

        });

        row.addText("summa", "", Cellstyle.Readonly);
    }

    private buildRadprisjusteringRow(doc: HotDoc, diameterklass: Diameterklass, row: HotRow, raskModalService: RaskModalService) {
        if (this.canEdit) {
            row.add(BaseMatrix.settingsCellName, this.getEditRadprisDiameterklassCell(doc, diameterklass, raskModalService));
        }

        row.add(BaseMatrix.rowHeaderCellName, new HotCellText(`${diameterklass.startdiameter}`, Cellstyle.Readonly));

        this.grid.langdklasser.forEach(langdklass => {
            const justering = diameterklass.radprisJustering.find(s => s.startlangd === langdklass.startlangd);
            row.add(this.langdCellName(langdklass.startlangd),
                new RadprisjusteringNumberCell(justering, diameterklass, this.form, justering.justering, Cellstyle.Default | Cellstyle.AlignCenter,
                    s => justering.justering = s.newValue));
        });
    }

    private readonly onAddOrEditRadprisDiameter = (doc: HotDoc, diameter: Diameterklass, raskModalService: RaskModalService): void => {
        const availableDiameters = this.grid.diameterklasser.filter(d => d.radprisJustering.length === 0).map(x => x.startdiameter);
        const input = new PromptInput(diameter === null ? InputMode.isNew : InputMode.canDelete, {
            inputField: {
                value: diameter?.startdiameter,
                label: "Startdiameter",
                values: of(availableDiameters),
                type: "simple-selector"
            }
        });

        input.description = "Välj startdiameter";

        raskModalService.prompt("Ange startdiameter", input)
            .subscribe(x => {
                if (!x.cancel) {
                    this.handleGridAdjustment(doc, input, x.fields?.inputField, x, (startdiameter) => {
                        // Delete
                        this.grid.clearRadprisjusteringar(diameter.startdiameter);
                    }, (startdiameter) => {
                        // Add
                        this.grid.addRadprisjustering(startdiameter);
                    }, (old, newVal) => {
                        if (old !== newVal) {
                            // Move
                            this.grid.moveRadprisjustering(diameter.startdiameter, newVal);
                        }
                    });

                    if (x.action === Action.none) {
                        // Cancel
                        return;
                    }

                    this.form.markAsDirty();
                    doc.rebind();
                }
            });
    };

    private readonly buildRadprisjusteringarColumnDefinitions = (editableCollSettings: { readOnly: boolean; type: string; editor?: boolean }): IHotColumnDefinition[] => {
        const columns: IHotColumnDefinition[] = [];
        this.addEditColumn(columns, false);

        columns.push({
            header: this.getInverseTableHeaderText(),
            width: 70,
            readOnly: true,
            cellName: BaseMatrix.rowHeaderCellName
        });

        this.grid.langdklasser.forEach(langdklass => {
            columns.push({
                header: `<span>${langdklass.startlangd}</span>`,
                cellName: this.langdCellName(langdklass.startlangd),
                type: editableCollSettings.type,
                readOnly: editableCollSettings.readOnly,
                editor: editableCollSettings.editor
            });
        });

        return columns;
    };

    private readonly buildRadprisjusteringarRowDefinitions = (doc: HotDoc, raskModalService: RaskModalService): void => {
        this.grid.diameterklasser
            .filter(p => p.radprisJustering.length > 0)
            .forEach((diameterklass) => {
                doc.createRow((row) => this.buildRadprisjusteringRow(doc, diameterklass, row, raskModalService));
            });
        doc.createRow(row => this.buildAddPrisjusteringRow(row, doc, raskModalService));
    };

}
